import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import SplashScreen from "./components/SplashScreen";
import Login from "./pages/Login";
import LandingPage from "./pages/LandingPage";
import DemoView from "./pages/DemoView";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import EventSelector from "./pages/EventSelector";
import Dashboard from "./pages/Dashboard";
import ResourceMap from "./pages/ResourceMap";
import Kalender from "./pages/Kalender";
import Evaluering from "./pages/Evaluering";
import Vagtplaner from "./pages/Vagtplaner";
import Materiel from "./pages/Materiel";
import Lager from "./pages/Lager";
import QRScan from "./pages/QRScan";
import QRRefRedirect from "./pages/QRRefRedirect";
import Analytics from "./pages/Analytics";
import PrintMap from "./pages/PrintMap";
import PrintAllQR from "./pages/PrintAllQR";
import QuickCheckinScan from "./pages/QuickCheckinScan";
import PrintQuickCheckin from "./pages/PrintQuickCheckin";
import Stamdata from "./pages/Stamdata";
import Notifikationer from "./pages/Notifikationer";
import LiveMonitor from "./pages/LiveMonitor";
import Frivillige from "./pages/Frivillige";
import Idebank from "./pages/Idebank";
import Beskeder from "./pages/Beskeder";
import LederOversigt from "./pages/LederOversigt";
import PlatformSettings from "./pages/PlatformSettings";
import SetupLog from "./pages/SetupLog";
import VigtigeInfo from "./pages/VigtigeInfo";
import Brugsvejledning from "./pages/Brugsvejledning";
import Profil from "./pages/Profil";
import { Toaster } from "@/components/ui/sonner";
import { PermissionsProvider } from "./contexts/PermissionsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { MapPerformanceProvider } from "./contexts/MapPerformanceContext";
import { useWebPush } from "./hooks/useWebPush";
import PoweredByBadge from "./components/PoweredByBadge";
import MobileBottomNav from "./components/MobileBottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BUILD_TIME = new Date().toLocaleString('da-DK', { timeZone: 'Europe/Copenhagen' });

export { API };

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  // Splash vises kun ved cold start (sessionStorage nulstilles når browser-tab lukkes
  // helt — så warm resume fra fx app-switcheren undgår splash, mens et helt frisk
  // app-launch fra hjemmeskærmen viser den).
  const isQrScanRoute = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/scan/') || window.location.pathname.startsWith('/quick-checkin/')
  );
  const [showSplash, setShowSplash] = useState(
    typeof window !== 'undefined' && !isQrScanRoute && !sessionStorage.getItem('resoviaSplashShown')
  );
  const [selectedEvent, setSelectedEvent] = useState(
    localStorage.getItem('selectedEventId') ? {
      id: localStorage.getItem('selectedEventId'),
      name: localStorage.getItem('selectedEventName'),
      archived: localStorage.getItem('selectedEventArchived') === 'true',
      logo_url: localStorage.getItem('selectedEventLogoUrl') || null
    } : null
  );

  // Web Push Notifications
  const { unsubscribePush } = useWebPush(token, !!user);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => setUser(res.data))
      .catch(() => {
        localStorage.removeItem('token');
        setToken(null);
      });
    }
  }, [token]);

  // Periodisk auto-refresh af bruger (hver 60. sek) — sikrer at fx gæste-tilladelser
  // opdateres i live-session når admin ændrer dem. Lille GET (~1kb) — ingen mærkbar effekt.
  useEffect(() => {
    if (!token) return;
    const interval = setInterval(() => {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setUser(prev => {
            if (!prev) return res.data;
            // Kun opdater hvis visible_layers eller rolle ændret (undgår unødvendige renders)
            const oldLayers = JSON.stringify(prev.visible_layers || {});
            const newLayers = JSON.stringify(res.data.visible_layers || {});
            if (oldLayers !== newLayers || prev.role !== res.data.role || prev.name !== res.data.name) {
              return { ...prev, ...res.data };
            }
            return prev;
          });
        })
        .catch((err) => {
          // Hvis token er blevet ugyldig (fx gæst udløbet/slettet) → log ud
          if (err?.response?.status === 401) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        });
    }, 60000);
    return () => clearInterval(interval);
  }, [token]);

  // Auto-login via direct-login QR-token (?guest_token=...)
  useEffect(() => {
    if (token) return;
    const params = new URLSearchParams(window.location.search);
    const guestToken = params.get('guest_token');
    if (!guestToken) return;
    axios.post(`${API}/auth/guest-direct-login`, { token: guestToken })
      .then(res => {
        // Ryd token fra URL så browser-historik ikke gemmer den
        const url = new URL(window.location.href);
        url.searchParams.delete('guest_token');
        window.history.replaceState({}, '', url.toString());
        handleLogin(res.data.token, res.data.user);
      })
      .catch(err => {
        const msg = err?.response?.data?.detail || 'Ugyldigt eller udløbet link';
        alert(`Gæsteadgang fejlede: ${msg}`);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    
    // Auto-select event for guests
    if (userData?.role === 'gæst' && userData?.assigned_event_ids?.length === 1) {
      const guestEventId = userData.assigned_event_ids[0];
      axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${newToken}` } })
        .then(res => {
          const ev = res.data.find(e => e.id === guestEventId);
          if (ev) handleSelectEvent(ev);
        }).catch(() => {});
    }
  };

  const handleLogout = () => {
    unsubscribePush();
    // Stop al GPS-tracking ved rigtig logout (GDPR). Almindelig refresh bevarer tracking.
    Object.keys(localStorage)
      .filter(k => k.startsWith('activeGPSTracking_'))
      .forEach(k => localStorage.removeItem(k));
    localStorage.removeItem('token');
    localStorage.removeItem('selectedEventId');
    localStorage.removeItem('selectedEventName');
    localStorage.removeItem('selectedEventArchived');
    localStorage.removeItem('selectedEventLogoUrl');
    setToken(null);
    setUser(null);
    setSelectedEvent(null);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    localStorage.setItem('selectedEventId', event.id);
    localStorage.setItem('selectedEventName', event.name);
    localStorage.setItem('selectedEventArchived', event.archived ? 'true' : 'false');
    if (event.logo_url) localStorage.setItem('selectedEventLogoUrl', event.logo_url);
    else localStorage.removeItem('selectedEventLogoUrl');
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
    localStorage.removeItem('selectedEventId');
    localStorage.removeItem('selectedEventName');
    localStorage.removeItem('selectedEventArchived');
    localStorage.removeItem('selectedEventLogoUrl');
  };

  return (
    <div className="App">
      {showSplash && (
        <SplashScreen
          onDone={() => {
            sessionStorage.setItem('resoviaSplashShown', '1');
            setShowSplash(false);
          }}
        />
      )}
      <ThemeProvider>
      <MapPerformanceProvider>
      <PermissionsProvider user={user}>
        <BrowserRouter>
          <PoweredByBadge />
          <Routes>
          {/* Offentlige ruter */}
          <Route path="/scan/:pointId" element={<QRScan />} />
          <Route path="/scan/q/:qrRef" element={<QRRefRedirect />} />
          <Route path="/quick-checkin/:token" element={<QuickCheckinScan />} />
          <Route path="/glemt-adgangskode" element={<ForgotPassword />} />
          <Route path="/nulstil-adgangskode" element={<ResetPassword />} />
          <Route path="/demo/:linkId" element={<DemoView />} />
          
          {!token ? (
            <>
              <Route path="/login" element={<Login onLogin={handleLogin} />} />
              <Route path="/brugsvejledning" element={<Brugsvejledning user={null} />} />
              <Route path="/" element={<LandingPage />} />
              <Route path="*" element={<LandingPage />} />
            </>
          ) : !selectedEvent ? (
            <>
              <Route path="/idebank" element={
                <Idebank 
                  user={user} 
                  onLogout={handleLogout}
                  onBack={() => window.history.back()}
                />
              } />
              <Route path="/brugsvejledning" element={<Brugsvejledning user={user} />} />
              <Route path="/profil" element={<Profil user={user} onLogout={handleLogout} />} />
              <Route path="/beskeder" element={
                <Beskeder 
                  user={user} 
                  selectedEvent={selectedEvent}
                />
              } />
              <Route path="*" element={
                <EventSelector 
                  user={user} 
                  onLogout={handleLogout}
                  onSelectEvent={handleSelectEvent}
                />
              } />
            </>
          ) : (
            <>
              <Route path="/" element={
                <Dashboard 
                  user={user} 
                  onLogout={handleLogout}
                  selectedEvent={selectedEvent}
                  onBackToEvents={handleBackToEvents}
                />
              } />
              <Route path="/ressourcehandtering" element={
                <ResourceMap 
                  user={user} 
                  onLogout={handleLogout}
                  selectedEvent={selectedEvent}
                />
              } />
              <Route path="/live-monitor" element={
                <LiveMonitor 
                  user={user} 
                  selectedEvent={selectedEvent}
                />
              } />
              <Route path="/frivillige" element={
                <Frivillige 
                  user={user} 
                  onLogout={handleLogout}
                  selectedEvent={selectedEvent}
                />
              } />
              <Route path="/kalender" element={<Kalender user={user} onLogout={handleLogout} />} />
              <Route path="/evaluering" element={<Evaluering user={user} onLogout={handleLogout} />} />
              <Route path="/vagtplaner" element={<Vagtplaner user={user} onLogout={handleLogout} />} />
              <Route path="/materiel" element={<Materiel user={user} onLogout={handleLogout} />} />
              <Route path="/lager" element={<Lager user={user} onLogout={handleLogout} />} />
              <Route path="/analytics" element={<Analytics user={user} onLogout={handleLogout} />} />
              <Route path="/event/:eventId/analytics" element={<Analytics user={user} onLogout={handleLogout} />} />
              <Route path="/print-map" element={<PrintMap user={user} selectedEvent={selectedEvent} />} />
              <Route path="/print-all-qr" element={<PrintAllQR user={user} selectedEvent={selectedEvent} />} />
              <Route path="/print-quick-checkin" element={<PrintQuickCheckin />} />
              <Route path="/stamdata" element={<Stamdata user={user} onLogout={handleLogout} selectedEvent={selectedEvent} />} />
              <Route path="/notifikationer" element={<Notifikationer user={user} selectedEvent={selectedEvent} />} />
              <Route path="/beskeder" element={<Beskeder user={user} selectedEvent={selectedEvent} />} />
              <Route path="/leder" element={<LederOversigt user={user} selectedEvent={selectedEvent} />} />
              <Route path="/setup-log" element={<SetupLog selectedEvent={selectedEvent} />} />
              <Route path="/vigtige-info" element={<VigtigeInfo user={user} />} />
              <Route path="/event/:eventId/vigtige-info" element={<VigtigeInfo user={user} />} />
              <Route path="/gps-tracking" element={<Navigate to="/ressourcehandtering" replace />} />
              <Route path="/idebank" element={
                <Idebank 
                  user={user} 
                  onLogout={handleLogout}
                  onBack={() => window.history.back()}
                />
              } />
              <Route path="/platform-settings" element={<PlatformSettings user={user} />} />
              <Route path="/brugsvejledning" element={<Brugsvejledning user={user} />} />
              <Route path="/profil" element={<Profil user={user} onLogout={handleLogout} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </>
          )}
        </Routes>
        <MobileBottomNav />
      </BrowserRouter>
      </PermissionsProvider>
      </MapPerformanceProvider>
      </ThemeProvider>
      {user?.role === 'admin' && (
        <div 
          data-testid="admin-version-badge"
          className="no-print"
          style={{
            position: 'fixed', bottom: 4, left: 4, 
            fontSize: '10px', color: 'rgba(120,120,120,0.6)', 
            background: 'rgba(255,255,255,0.8)', padding: '2px 6px',
            borderRadius: 4, zIndex: 9999, pointerEvents: 'none',
            fontFamily: 'monospace'
          }}
        >
          Build: {BUILD_TIME}
        </div>
      )}
      <Toaster position="bottom-left" />
    </div>
  );
}

export default App;
